import {
  AMBIENT_PREDATOR_LEVEL_MAX_SCORE,
  DAY_NIGHT_AMBIENT_PREDATOR_MODIFIER,
  DEFEAT_POPULATION,
  FAMINE_STREAK_REQUIRED,
  FIRE_DURATION_TURNS_MAX,
  FIRE_DURATION_TURNS_MIN,
  FIRE_EVENT_PROBABILITY,
  FOOD_BANK_GROWTH_COST,
  FOOD_BANK_GROWTH_THRESHOLD,
  GROWTH_STREAK_REQUIRED,
  INITIAL_FOOD_SUPPLIES,
  INITIAL_POPULATION,
  INITIAL_TURN,
  SEASON_AMBIENT_PREDATOR_MODIFIER,
  SEASON_WATER_MULTIPLIER,
  TERRAIN_AMBIENT_PREDATOR_BASE,
  TERRAIN_BASE_WATER,
  TERRAIN_FOOD_PRODUCTION,
  TERRAIN_SPAWN_WEIGHTS,
  TILE_FOOD_DEPLETION_STEP,
  TILE_FOOD_MIN_YIELD_FRACTION,
  TILE_FOOD_REGEN_STEP,
  VICTORY_POPULATION,
  type DayNightPhase,
  type Season,
  type TileTerrain,
} from "@dawn/engine";

export interface CoreBalance {
  initialPopulation: number;
  initialFoodSupplies: number;
  initialTurn: number;
  victoryPopulation: number;
  defeatPopulation: number;
  foodBankGrowthThreshold: number;
  foodBankGrowthCost: number;
  growthStreakRequired: number;
  famineStreakRequired: number;
  tileFoodDepletionStep: number;
  tileFoodRegenStep: number;
  tileFoodMinYieldFraction: number;
  terrainFoodProduction: Record<TileTerrain, number>;
  terrainBaseWater: Record<TileTerrain, number>;
  seasonWaterMultiplier: Record<Season, number>;
  terrainAmbientPredatorBase: Record<TileTerrain, number>;
  seasonAmbientPredatorModifier: Record<Season, number>;
  dayNightAmbientPredatorModifier: Record<DayNightPhase, number>;
  ambientPredatorLevelMaxScore: {
    none: number;
    low: number;
    medium: number;
  };
  terrainSpawnWeights: Array<{ terrain: TileTerrain; weight: number }>;
  settleTileInfoDelayMs: number;
  settleReadyToPlayDelayMs: number;
  fireEventProbability: number;
  fireDurationTurnsMin: number;
  fireDurationTurnsMax: number;
}

export const DEFAULT_CORE_BALANCE: CoreBalance = {
  initialPopulation: INITIAL_POPULATION,
  initialFoodSupplies: INITIAL_FOOD_SUPPLIES,
  initialTurn: INITIAL_TURN,
  victoryPopulation: VICTORY_POPULATION,
  defeatPopulation: DEFEAT_POPULATION,
  foodBankGrowthThreshold: FOOD_BANK_GROWTH_THRESHOLD,
  foodBankGrowthCost: FOOD_BANK_GROWTH_COST,
  growthStreakRequired: GROWTH_STREAK_REQUIRED,
  famineStreakRequired: FAMINE_STREAK_REQUIRED,
  tileFoodDepletionStep: TILE_FOOD_DEPLETION_STEP,
  tileFoodRegenStep: TILE_FOOD_REGEN_STEP,
  tileFoodMinYieldFraction: TILE_FOOD_MIN_YIELD_FRACTION,
  terrainFoodProduction: { ...TERRAIN_FOOD_PRODUCTION },
  terrainBaseWater: { ...TERRAIN_BASE_WATER },
  seasonWaterMultiplier: { ...SEASON_WATER_MULTIPLIER },
  terrainAmbientPredatorBase: { ...TERRAIN_AMBIENT_PREDATOR_BASE },
  seasonAmbientPredatorModifier: { ...SEASON_AMBIENT_PREDATOR_MODIFIER },
  dayNightAmbientPredatorModifier: { ...DAY_NIGHT_AMBIENT_PREDATOR_MODIFIER },
  ambientPredatorLevelMaxScore: { ...AMBIENT_PREDATOR_LEVEL_MAX_SCORE },
  terrainSpawnWeights: TERRAIN_SPAWN_WEIGHTS.map((entry) => ({ ...entry })),
  settleTileInfoDelayMs: 2000,
  settleReadyToPlayDelayMs: 2000,
  fireEventProbability: FIRE_EVENT_PROBABILITY,
  fireDurationTurnsMin: FIRE_DURATION_TURNS_MIN,
  fireDurationTurnsMax: FIRE_DURATION_TURNS_MAX,
};

let effectiveCoreBalance: CoreBalance = structuredClone(DEFAULT_CORE_BALANCE);

export function getCoreBalance(): CoreBalance {
  return effectiveCoreBalance;
}

export function installCoreBalance(balance: CoreBalance): void {
  effectiveCoreBalance = structuredClone(balance);
}

export function resetCoreBalanceForTests(): void {
  effectiveCoreBalance = structuredClone(DEFAULT_CORE_BALANCE);
}
